import React, { useState, useEffect } from "react";
import Loader from "../../../loader/Loader";
import { toast } from "react-toastify";
import axios from "axios";
import { domain } from "../../../../security";
import { useSelector } from "react-redux";
import { selectUserID } from "../../../../redux/IchthusSlice";
import { FaTimes } from "react-icons/fa";

const AddApprover = ({ onClose, refreshData, approverToEdit }) => {
  const currentUserID = useSelector(selectUserID); // ✅ audit

  const [formData, setFormData] = useState({ userId: "" });
  const [usersList, setUsersList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [fetchingUsers, setFetchingUsers] = useState(true);

  // Fetch active users for dropdown
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        // ✅ Fetch both all users AND current active approvers simultaneously
        const [usersRes, approversRes] = await Promise.all([
          axios.get(`${domain}/api/Users`),
          axios.get(`${domain}/api/Approvers`),
        ]);

        const allUsers = usersRes.data;
        const activeApprovers = approversRes.data;

        // ✅ Get the IDs of users who are already active approvers
        const alreadyApproverIds = new Set(
          activeApprovers.map((a) => a.approverUserId),
        );

        // ✅ When editing, keep the currently assigned user in the list
        // so the dropdown doesn't show an empty selection
        const filtered = allUsers.filter(
          (u) =>
            !alreadyApproverIds.has(u.id) ||
            (approverToEdit && u.id === approverToEdit.approverUserId),
        );

        setUsersList(filtered);
      } catch (error) {
        console.error("Error fetching users:", error);
        toast.error("Could not load users list.");
      } finally {
        setFetchingUsers(false);
      }
    };
    fetchUsers();
  }, [approverToEdit]);

  // Pre-populate when editing
  useEffect(() => {
    if (approverToEdit) {
      setFormData({ userId: approverToEdit.approverUserId }); // ✅ was approverToEdit.userId
    } else {
      setFormData({ userId: "" });
    }
  }, [approverToEdit]);

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setFormData({ ...formData, [id]: value });
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    if (!formData.userId) {
      toast.warning("Please select a user.");
      return;
    }

    setIsLoading(true);
    const apiUrl = `${domain}/api/Approvers`;

    const payload = {
      approverUserId: formData.userId, // ✅ renamed
      UserID: currentUserID,
    };
    try {
      if (approverToEdit) {
        // PUT — update existing approver
        await axios.put(
          `${apiUrl}/${approverToEdit.id}`, // ✅ was approverToEdit.approverId
          payload,
        );
        toast.success("Approver updated successfully");
      } else {
        // POST — add new approver
        await axios.post(apiUrl, payload);
        toast.success("Approver added successfully");
      }

      refreshData();
      onClose();
    } catch (error) {
      const errMsg = error.response?.data || error.message;
      toast.error(`Error: ${errMsg}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg flex flex-col">
      {isLoading && <Loader />}

      {/* Header */}
      <div className="flex items-center justify-between p-5 border-b border-gray-200">
        <h2 className="text-xl font-semibold text-gray-800">
          {approverToEdit ? "Edit Approver" : "Add Approver"}
        </h2>
        <button
          onClick={onClose}
          className="p-2 rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-800 transition-colors"
        >
          <FaTimes className="h-5 w-5" />
        </button>
      </div>

      {/* Body */}
      <div className="p-6">
        <form onSubmit={handleFormSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Select User
            </label>
            {fetchingUsers ? (
              <div className="text-sm text-gray-500 animate-pulse py-2">
                Loading users...
              </div>
            ) : (
              <select
                id="userId"
                className="border border-gray-300 rounded-lg py-2.5 px-4 w-full focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white text-slate-700"
                value={formData.userId}
                onChange={handleInputChange}
              >
                <option value="">-- Select a User --</option>
                {usersList.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.employeeName} (@{user.userName})
                  </option>
                ))}
              </select>
            )}
            <p className="text-xs text-gray-400 mt-1.5">
              Select a user to grant approval privileges.
            </p>
          </div>

          {/* Footer */}
          <div className="pt-2">
            <button
              type="submit"
              disabled={fetchingUsers || isLoading}
              className={`w-full py-3 text-center text-white rounded-lg font-semibold transition-all shadow-md
                ${
                  fetchingUsers || isLoading
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-indigo-600 hover:bg-indigo-700"
                }`}
            >
              {approverToEdit ? "Update Approver" : "Add Approver"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddApprover;
